"use client";

import { useState } from "react";
import { ChevronDown, Minus, Plus } from "lucide-react";

type CollapsibleProps = Readonly<{
  title: string;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** When true, uses +/- icons and only collapses on mobile (footer). When false, uses chevron (filters). */
  plusIcon?: boolean;
}>;

export default function Collapsible({
  title,
  children,
  defaultOpen = false,
  className = "",
  plusIcon = false,
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const panelClassName = [
    "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
    plusIcon ? "md:grid-rows-[1fr]!" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={
        plusIcon
          ? `w-full ${className}`
          : `border-b border-gray-100 last:border-b-0 ${className}`
      }
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={
          plusIcon
            ? "w-full flex items-center justify-between py-3 text-white text-sm font-medium hover:text-Accent transition-colors border-b border-b-gray-200 md:hidden"
            : "w-full flex items-center justify-between py-3.5 text-right"
        }
        aria-expanded={isOpen}
      >
        {plusIcon ? (
          <>
            <h3>{title}</h3>
            {isOpen ? (
              <Minus className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-gray-800">{title}</span>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 shrink-0 transition-transform duration-300 motion-reduce:transition-none ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      <div className={panelClassName}>
        <div
          className={`min-h-0 ${isOpen ? "overflow-visible" : "overflow-hidden"}`}
        >
          {children != null && (
            <div
              className={
                plusIcon
                  ? "pt-3 md:pt-0"
                  : "flex flex-col gap-4 pb-4"
              }
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
